import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { PplxStreamError } from '#pplx/frontend/stream/errors.js';
import { ingestMessage, useStreamStore } from '#pplx/frontend/stream/stream.js';
import { addEntryToThreads } from '#pplx/frontend/stream/utils.js';
import { useCallback, useEffect, useRef } from 'react';
const SYNCHRONOUS_INITIAL_ENTRIES_LIMIT = 20;
const INITIAL_ENTRIES_CHUNK_SIZE = 20;
function getEntryCreatedDatetimeKey(entries, entryId, currentIndex) {
    const timestamp = entries[entryId]?.entry_created_datetime;
    if (!timestamp)
        return null;
    const timestampMs = Date.parse(timestamp);
    if (Number.isNaN(timestampMs))
        return null;
    return { entryId, currentIndex, timestamp, timestampMs };
}
function compareEntryCreatedDatetimeKeys(a, b) {
    const timestampMsDiff = a.timestampMs - b.timestampMs;
    if (timestampMsDiff !== 0)
        return timestampMsDiff;
    const timestampDiff = a.timestamp.localeCompare(b.timestamp);
    if (timestampDiff !== 0)
        return timestampDiff;
    return a.currentIndex - b.currentIndex;
}
function reorderThreadsByEntryCreatedDatetime(threads, entries, threadIdsToReorder) {
    if (!threadIdsToReorder.size)
        return threads;
    let changed = false;
    const nextThreads = threads.map((thread) => {
        if (!threadIdsToReorder.has(thread.id))
            return thread;
        const nextEntryIds = [];
        let timestampedRun = [];
        const flushTimestampedRun = () => {
            if (!timestampedRun.length)
                return;
            nextEntryIds.push(...timestampedRun.sort(compareEntryCreatedDatetimeKeys).map(({ entryId }) => entryId));
            timestampedRun = [];
        };
        for (const [currentIndex, entryId] of thread.entryIds.entries()) {
            const orderKey = getEntryCreatedDatetimeKey(entries, entryId, currentIndex);
            if (!orderKey) {
                // Ordering is best-effort for older/live entries until the server provides timestamps.
                flushTimestampedRun();
                nextEntryIds.push(entryId);
                continue;
            }
            timestampedRun.push(orderKey);
        }
        flushTimestampedRun();
        const threadChanged = nextEntryIds.length !== thread.entryIds.length ||
            nextEntryIds.some((entryId, index) => entryId !== thread.entryIds[index]);
        if (!threadChanged)
            return thread;
        changed = true;
        return { ...thread, entryIds: nextEntryIds };
    });
    return changed ? nextThreads : threads;
}
function backfillEntryCreatedDatetime(entries, entriesForCreatedDatetimeBackfill) {
    let nextEntries = null;
    for (const entry of entriesForCreatedDatetimeBackfill) {
        const existingEntry = (nextEntries ?? entries)[entry.backend_uuid];
        if (!existingEntry)
            continue;
        if (existingEntry.entry_created_datetime)
            continue;
        if (!entry.entry_created_datetime)
            continue;
        if (!nextEntries) {
            nextEntries = { ...entries };
        }
        nextEntries[entry.backend_uuid] = {
            ...existingEntry,
            entry_created_datetime: entry.entry_created_datetime,
        };
    }
    return nextEntries ?? entries;
}
function synchronizeThreadsWithEntryOrder(threads, entryOrder) {
    const entryRanks = new Map(entryOrder.map((entry, rank) => [entry.backend_uuid, rank]));
    const threadIds = new Set(entryOrder.map((entry) => entry.context_uuid));
    let changed = false;
    const nextThreads = threads.map((thread) => {
        if (!threadIds.has(thread.id))
            return thread;
        // A fetched page may omit older cached entries, so only reorder its occupied slots.
        const rankedEntryIds = thread.entryIds
            .filter((entryId) => entryRanks.has(entryId))
            .sort((a, b) => entryRanks.get(a) - entryRanks.get(b));
        let rankIndex = 0;
        const nextEntryIds = thread.entryIds.map((entryId) => entryRanks.has(entryId) ? rankedEntryIds[rankIndex++] : entryId);
        const threadChanged = nextEntryIds.some((entryId, index) => entryId !== thread.entryIds[index]);
        if (!threadChanged)
            return thread;
        changed = true;
        return { ...thread, entryIds: nextEntryIds };
    });
    return changed ? nextThreads : threads;
}
/**
 * Initialize the threads in the stream state
 * @param entries - Entries to initialize
 * @returns A function that initializes the threads
 */
export const useInitializeThreads = () => {
    const store = useStreamStore();
    const queuedEntriesRef = useRef([]);
    const pendingEntryIdsRef = useRef(new Set());
    const scheduledTaskRef = useRef(null);
    const ingestQueuedEntries = useCallback((queuedEntries, options) => {
        const { orderByEntryCreatedDatetime } = options;
        const entriesForCreatedDatetimeBackfill = options.entriesForCreatedDatetimeBackfill ?? [];
        const entryOrder = options.entryOrder ?? [];
        const threadIdsToOrderByEntryCreatedDatetime = new Set();
        for (const { entry } of queuedEntries) {
            threadIdsToOrderByEntryCreatedDatetime.add(entry.context_uuid);
        }
        for (const entry of entriesForCreatedDatetimeBackfill) {
            threadIdsToOrderByEntryCreatedDatetime.add(entry.context_uuid);
        }
        store.setState((state) => {
            let nextState = state;
            let changed = false;
            for (const { entry, missingEntryPosition } of queuedEntries) {
                // We have already ingested this entry, skip
                if (nextState.entries[entry.backend_uuid]) {
                    continue;
                }
                changed = true;
                const streamId = entry.frontend_uuid ?? safeRandomUUID();
                const isFinal = !entry.reconnectable;
                const threadId = entry.context_uuid;
                const entryId = entry.backend_uuid;
                try {
                    const nextPartialState = ingestMessage(nextState, {
                        ...entry,
                        final: isFinal,
                        final_sse_message: isFinal,
                    }, { streamId, threadEntryPosition: missingEntryPosition });
                    nextState = { ...nextState, ...nextPartialState };
                }
                catch (error) {
                    // If we're failing to ingest during initialize here,
                    // don't add it to erroredStreams bc it's not retryable.
                    // Just set the status to FAILED.
                    const wrappedError = new PplxStreamError('STREAM_INITIAL_ENTRIES_ERROR', {
                        message: 'Error in initializeThreads',
                        cause: error,
                        details: {
                            request_id: streamId,
                            backend_uuid: entryId,
                        },
                    });
                    logger.error(wrappedError);
                    // We're still adding entries and threads here so we can render
                    // the error state properly.
                    nextState = {
                        ...nextState,
                        threads: addEntryToThreads(nextState.threads, threadId, entryId, {
                            position: missingEntryPosition,
                        }),
                        entries: {
                            ...nextState.entries,
                            [entryId]: {
                                ...entry,
                                status: 'FAILED',
                            },
                        },
                    };
                }
            }
            if (entriesForCreatedDatetimeBackfill.length) {
                const nextEntries = backfillEntryCreatedDatetime(nextState.entries, entriesForCreatedDatetimeBackfill);
                if (nextEntries !== nextState.entries) {
                    changed = true;
                    nextState = { ...nextState, entries: nextEntries };
                }
            }
            if (entryOrder.length) {
                const nextThreads = synchronizeThreadsWithEntryOrder(nextState.threads, entryOrder);
                if (nextThreads !== nextState.threads) {
                    changed = true;
                    nextState = { ...nextState, threads: nextThreads };
                }
            }
            if (orderByEntryCreatedDatetime) {
                const nextThreads = reorderThreadsByEntryCreatedDatetime(nextState.threads, nextState.entries, threadIdsToOrderByEntryCreatedDatetime);
                if (nextThreads !== nextState.threads) {
                    changed = true;
                    nextState = { ...nextState, threads: nextThreads };
                }
            }
            return changed ? nextState : state;
        }, undefined, 'ingestInitialEntries');
        for (const { entry } of queuedEntries) {
            pendingEntryIdsRef.current.delete(entry.backend_uuid);
        }
    }, [store]);
    const scheduleQueuedEntriesDrain = useCallback(() => {
        if (scheduledTaskRef.current != null)
            return;
        scheduledTaskRef.current = setTimeout(() => {
            scheduledTaskRef.current = null;
            const chunk = queuedEntriesRef.current.splice(0, INITIAL_ENTRIES_CHUNK_SIZE);
            if (!chunk.length)
                return;
            ingestQueuedEntries(chunk, {
                orderByEntryCreatedDatetime: chunk.some((queuedEntry) => queuedEntry.orderByEntryCreatedDatetime),
            });
            if (queuedEntriesRef.current.length > 0) {
                scheduleQueuedEntriesDrain();
            }
        }, 0);
    }, [ingestQueuedEntries]);
    useEffect(() => () => {
        if (scheduledTaskRef.current != null) {
            clearTimeout(scheduledTaskRef.current);
        }
        queuedEntriesRef.current = [];
        pendingEntryIdsRef.current.clear();
    }, []);
    const initializeThreads = useCallback(
    /**
     * Initialize the threads in the stream state
     * @param entries - Entries to initialize
     * @param options - Where to insert entries that are missing from the store.
     */
    (entries, options) => {
        const missingEntryPosition = options?.missingEntryPosition ?? 'append';
        const synchronizeEntryOrder = options?.synchronizeEntryOrder ?? false;
        const orderByEntryCreatedDatetime = missingEntryPosition === 'prepend' || synchronizeEntryOrder;
        // Reverse so that prepending one-by-one produces the original order.
        const entriesToInitialize = missingEntryPosition === 'prepend' ? [...entries].reverse() : entries;
        const existingEntries = store.getState().entries;
        const entriesToQueue = entriesToInitialize.filter((entry) => {
            if (existingEntries[entry.backend_uuid])
                return false;
            if (pendingEntryIdsRef.current.has(entry.backend_uuid))
                return false;
            pendingEntryIdsRef.current.add(entry.backend_uuid);
            return true;
        });
        if (entriesToQueue.length <= SYNCHRONOUS_INITIAL_ENTRIES_LIMIT || synchronizeEntryOrder) {
            ingestQueuedEntries(entriesToQueue.map((entry) => ({
                entry,
                missingEntryPosition,
                orderByEntryCreatedDatetime,
            })), {
                orderByEntryCreatedDatetime,
                entriesForCreatedDatetimeBackfill: orderByEntryCreatedDatetime ? entries : undefined,
                entryOrder: synchronizeEntryOrder ? entries : undefined,
            });
            return;
        }
        if (orderByEntryCreatedDatetime) {
            ingestQueuedEntries([], {
                orderByEntryCreatedDatetime,
                entriesForCreatedDatetimeBackfill: entries,
            });
        }
        queuedEntriesRef.current.push(...entriesToQueue.map((entry) => ({
            entry,
            missingEntryPosition,
            orderByEntryCreatedDatetime,
        })));
        scheduleQueuedEntriesDrain();
    }, [ingestQueuedEntries, scheduleQueuedEntriesDrain, store]);
    return initializeThreads;
};
//# sourceMappingURL=useInitializeThreads.js.map